import webBiLogger from '@wix/web-bi-logger';
import { BI_EVENTS, biEventsMap, biConstants } from './biEvents';
import wix from '../services/wix';
import { ChatroomDisplayData } from '../components/Widgets/SocialChat/SocialChat';

let logger;

export const initLogger = ({
  instanceId: app_instance_id,
  biToken: bi_token,
  visitorId: visitor_id,
  viewMode: mode,
  isSocial: is_social,
  isBusiness: is_business,
}: any) => {
  logger = webBiLogger
    .factory({ endpoint: biConstants.BI_END_POINT })
    .setEvents(biEventsMap)
    .setDefaults({
      src: biConstants.BI_ENGAGE_SRC,
      app_instance_id,
      bi_token,
      visitor_id,
      is_social,
      is_business,
      mode,
    })
    .logger();
};

export const sendMessageEvent = () => {
  const mode = wix.getViewMode();
  return logger.log(BI_EVENTS.SEND_MESSAGE, { mode });
};

export const expandChatEvent = ({ direction, referrer }) => {
  const mode = wix.getViewMode();
  return logger.log(BI_EVENTS.EXPAND_CHAT, {
    mode,
    direction,
    triggerBy: referrer,
  });
};

export const messageReceivedEvent = shard_id => {
  const mode = wix.getViewMode();
  return logger.log(BI_EVENTS.REALTIME_ACTIVITY, { mode, shard_id });
};

export const sendDebug = ({ tag, description }) => {
  const mode = wix.getViewMode();
  return logger.log(BI_EVENTS.DEBUG, { tagId: tag, description, mode });
};

export const attachmentClickEvent = () => {
  const mode = wix.getViewMode();
  return logger.log(BI_EVENTS.ADD_ATTACHMENT_CLICK, { mode });
};

export const imageClickEvent = () => {
  const mode = wix.getViewMode();
  return logger.log(BI_EVENTS.IMAGE_CLICK, { mode });
};

export const chatDisplayedEvent = (deviceType, layoutName) => {
  const mode = wix.getViewMode();
  const loadTime = Date.now() - window.startLoadTime;
  const isFullRender = /fullRender/.test(window.location.href);
  return logger.log(BI_EVENTS.CHAT_DISPLAYED, {
    platform: deviceType === 'mobile' ? 'QAB' : 'desktop',
    load_time: loadTime,
    is_full_render: isFullRender,
    layoutName,
    mode,
  });
};

export const callToActionClickEvent = url => {
  const mode = wix.getViewMode();
  return logger.log(BI_EVENTS.CALL_TO_ACTION_CLICK, { url, mode });
};

export const lcfSubmission = () => {
  const mode = wix.getViewMode();
  return logger.log(BI_EVENTS.LCF_SUBMISSION, { mode });
};

export const copyCouponCode = () => {
  const mode = wix.getViewMode();
  return logger.log(BI_EVENTS.COPY_COUPON_CODE, { mode });
};

export const socialNewConversion = entry_point => {
  const mode = wix.getViewMode();
  return logger.log(BI_EVENTS.SOCIAL_CHAT_NEW_CONVERSION, {
    entry_point,
    mode,
  });
};

export const socialSearchResults = result_number => {
  const mode = wix.getViewMode();
  return logger.log(BI_EVENTS.SOCIAL_CHAT_SEARCH_MEMBER, {
    result_number,
    mode,
  });
};

export const groupChatCreateStart = () => {
  return logger.log(BI_EVENTS.GROUP_CHAT_CREATE_START, {
    origin: 'web',
  });
};

export const groupChatCreateMemberPickerFilter = (
  members_displayed: number,
) => {
  return logger.log(BI_EVENTS.GROUP_CHAT_CREATE_MEMBER_PICKER_FILTER, {
    origin: 'web',
    referral: 'new group-members picker',
    members_displayed,
  });
};

export const groupChatCreateMemberPickerNext = (
  member_of_members_selected: number,
) => {
  return logger.log(BI_EVENTS.GROUP_CHAT_CREATE_MEMBER_PICKER_NEXT, {
    origin: 'web',
    referral: 'new group-members picker',
    member_of_members_selected,
  });
};

export const groupChatCreateMemberPickerBack = () => {
  return logger.log(BI_EVENTS.GROUP_CHAT_CREATE_MEMBER_PICKER_BACK, {
    origin: 'web',
    referral: 'new group-members picker',
  });
};

export const groupChatCreateMemberPickerSelect = (member_id: string) => {
  return logger.log(
    BI_EVENTS.GROUP_CHAT_CREATE_MEMBER_PICKER_CHECKBOX_CLICKED,
    {
      origin: 'web',
      referral: 'new group-members picker',
      member_id,
    },
  );
};

export const groupChatCreateMemberPickerRemove = (member_id: string) => {
  return logger.log(BI_EVENTS.GROUP_CHAT_CREATE_MEMBER_PICKER_REMOVE, {
    origin: 'web',
    referral: 'new group-members picker',
    member_id,
  });
};

export const groupChatCreateRoomInfoCreate = (
  number_of_members_selected: number,
) => {
  return logger.log(BI_EVENTS.GROUP_CHAT_CREATE_ROOM_INFO_CREATE, {
    origin: 'web',
    referral: 'new group-info',
    number_of_members_selected,
  });
};

export const groupChatCreateRoomInfoBack = () => {
  return logger.log(BI_EVENTS.GROUP_CHAT_CREATE_ROOM_INFO_BACK, {
    origin: 'web',
    referral: 'new group-info',
  });
};

export const groupChatCreateRoomInfoUploadAvatar = () => {
  return logger.log(BI_EVENTS.GROUP_CHAT_CREATE_ROOM_INFO_UPLOAD_AVATAR, {
    origin: 'web',
    referral: 'new group-info',
  });
};

export const generateGroupChatBi = (
  currentChatroom: ChatroomDisplayData,
  member_id: string,
): GroupChatBi => {
  const role = currentChatroom.createBy === member_id ? 'owner' : 'member';
  const chatroomId = currentChatroom.id;

  return new GroupChatBi('web', role, chatroomId, member_id);
};

export class GroupChatBi {
  chatroomParam: {
    origin: string;
    role: string;
    chatroomId: string;
    member_id: string;
  };

  constructor(
    origin: string,
    role: string,
    chatroomId: string,
    member_id: string,
  ) {
    this.chatroomParam = {
      origin,
      role,
      chatroomId,
      member_id,
    };
  }

  logger = (biEvents: { evid: number }, loggerParam: any) => {
    return logger.log(biEvents, {
      ...this.chatroomParam,
      ...loggerParam,
    });
  };

  groupChatLeaveConfirm = () => {
    return this.logger(BI_EVENTS.GROUP_CHAT_LEAVE_CONFIRM, {
      referral: 'group chat leave room',
    });
  };

  groupChatMenuSelected = (button: string) => {
    return this.logger(BI_EVENTS.GROUP_CHAT_MENU_SELECTED, {
      referral: 'group chat room options',
      chatRoomType: 'group chat',
      button,
    });
  };

  groupChatMenuClicked = () => {
    return this.logger(BI_EVENTS.GROUP_CHAT_MENU_CLICKED, {
      referral: 'group chat room',
      chatRoomType: 'group chat',
    });
  };

  groupChatMembersClickAdd = () => {
    return this.logger(BI_EVENTS.GROUP_CHAT_MEMBERS_CLICK_ADD, {
      referral: 'group chat info',
    });
  };

  groupChatMembersClickRemove = () => {
    return this.logger(BI_EVENTS.GROUP_CHAT_MEMBERS_CLICK_REMOVE, {
      referral: 'group chat info',
    });
  };

  groupChatMembersConfirmRemove = () => {
    return this.logger(BI_EVENTS.GROUP_CHAT_MEMBERS_CONFIRM_REMOVE, {
      referral: 'group chat info',
    });
  };

  groupChatMembersPickerCheckboxClicked = () => {
    return this.logger(BI_EVENTS.GROUP_CHAT_MEMBERS_PICKER_CHECKBOX_CLICKED, {
      referral: 'group chat info',
    });
  };

  groupChatMembersPickerRemove = () => {
    return this.logger(BI_EVENTS.GROUP_CHAT_MEMBERS_PICKER_REMOVE, {
      referral: 'group chat info',
    });
  };

  groupChatMembersConfirmAdd = () => {
    return this.logger(BI_EVENTS.GROUP_CHAT_MEMBERS_CONFIRM_ADD, {
      referral: 'chat group add member',
    });
  };

  groupChatInfoConfirm = () => {
    return this.logger(BI_EVENTS.GROUP_CHAT_INFO_CONFIRM, {
      referral: 'edit group details',
    });
  };

  groupChatInfoUploadAvatar = () => {
    return this.logger(BI_EVENTS.GROUP_CHAT_INFO_UPLOAD_AVATAR, {
      referral: 'group chat info',
    });
  };
}
